import LegacyImage, { ImageProps as LegacyProps } from 'next/legacy/image';
import NewImage, { ImageProps as NewProps } from 'next/image';
import { isMatch, isNotEmpty } from 'utility/functions';

import { DeepPartial } from 'types/swagger';
import React, { HTMLProps } from 'react';

export enum enLoading {
  lazy = 'lazy',
  eager = 'eager',
}

export type LoadingType = `${enLoading}` | undefined;

export type DynamicImageProps = DeepPartial<LegacyProps | NewProps> &
  HTMLProps<HTMLImageElement> & {
    fill?: boolean;
    overrideSrc?: string;
  };

export const DynamicImage = (props: DynamicImageProps) => {
  const {
    src,
    alt,
    fill,
    width,
    srcset,
    height,
    loader,
    layout,
    quality,
    loading,
    lazyRoot,
    priority,
    objectFit,
    overrideSrc,
    placeholder,
    unoptimized,
    blurDataURL,
    lazyBoundary,
    objectPosition,
    onError,
    onLoadingComplete,
    ...oth
  } = props as any;

  if (src && isNotEmpty(src)) {
    if (
      // when image has srcset consider it already optimized
      isNotEmpty(srcset) ||
      // SVG files are not handled by next-image
      isMatch(`${src}`, '\\.svg(\\?|$)') ||
      // data:image are not handled by next-image
      isMatch(
        `${src}`,
        '^data:image\\/(gif|png|jpeg|bmp|webp|svg\\+xml)(?:;charset=utf-8)?;base64,(?:[A-Za-z0-9]|[+\\/])+={0,2}$'
      )
    ) {
      let { fill, style, ...attribs } = props ?? {};

      if (fill) {
        style = style ?? {};
        style.objectFit = 'fill';
      }

      return <img {...attribs} style={style} src={src} />;
    } else {
      const isLegacy = [layout, objectFit, objectPosition].some((value) => isNotEmpty(value));
      const fetchpriority = loading === enLoading.eager ? 'high' : undefined;

      if (isLegacy) {
        return (
          <LegacyImage
            {...oth}
            src={src}
            alt={alt ?? ''}
            width={width}
            height={height}
            layout={layout}
            loader={loader}
            quality={quality}
            loading={loading}
            priority={priority}
            lazyRoot={lazyRoot}
            objectFit={objectFit}
            placeholder={placeholder}
            blurDataURL={blurDataURL}
            unoptimized={unoptimized}
            lazyBoundary={lazyBoundary}
            fetchpriority={fetchpriority}
            objectPosition={objectPosition}
            onLoadingComplete={onLoadingComplete}
            suppressHydrationWarning={true}
          />
        );
      }
      return (
        <NewImage
          {...oth}
          src={src}
          alt={alt ?? ''}
          fill={fill}
          width={width}
          height={height}
          loader={loader}
          quality={quality}
          loading={loading}
          priority={priority}
          lazyRoot={lazyRoot}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          unoptimized={unoptimized}
          overrideSrc={overrideSrc}
          lazyBoundary={lazyBoundary}
          fetchpriority={fetchpriority}
          onLoadingComplete={onLoadingComplete}
          suppressHydrationWarning={true}
        />
      );
    }
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <React.Fragment />;
};
